import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  StepButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  InputAdornment,
  Paper,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ContentCopy as CopyIcon, OpenInNew as OpenInNewIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { useAuth } from '@clerk/clerk-react';
import { useProject } from '../projects/ProjectContext';
import { defaultConfig, getConfig } from '../config/bundlerConfig';
import { addRecoverySnapshot } from '../utils/recoveryBackup';
import { QRCodeCanvas } from 'qrcode.react';

/** Etherscan sometimes returns `{ status:'failed', error:'…already verified…' }` — treat as success for UI/API. */
function normalizeLaunchVerification(v) {
  if (!v || typeof v !== 'object') return v;
  if (v.status === 'already_verified') return v;
  const err = String(v.error || v.message || '').trim();
  if (
    v.status === 'failed' &&
    /already\s+verified|contract\s+source\s+code\s+already|source\s+code\s+already\s+verified/i.test(err)
  ) {
    return { status: 'already_verified', message: err || 'Contract source code already verified' };
  }
  return v;
}

const MotionPaper = motion(Paper);

const pillSx = {
  borderRadius: 999,
  px: 2,
  fontWeight: 750,
  textTransform: 'none',
};

const gradientPrimarySx = {
  ...pillSx,
  backgroundImage:
    'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(99, 102, 241, 1) 45%, rgba(34, 211, 238, 1) 100%)',
  color: '#08090e',
  boxShadow: '0 8px 28px rgba(168, 85, 247, 0.26)',
  '&:hover': {
    backgroundImage:
      'linear-gradient(90deg, rgba(187, 110, 255, 1) 0%, rgba(115, 122, 255, 1) 45%, rgba(56, 225, 255, 1) 100%)',
    boxShadow: '0 10px 32px rgba(168, 85, 247, 0.32)',
  },
  '&.Mui-disabled': {
    backgroundImage: 'none',
    color: alpha('#e9e7ff', 0.42),
    boxShadow: 'none',
  },
};

const LaunchWizard = () => {
  const theme = useTheme();
  const accentPurple = theme.palette.primary.main;
  const accentCyan = theme.palette.secondary.main;
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);
  const { currentProjectId, projectSettings, refreshProjectSettings } = useProject();
  const currentProjectIdRef = useRef(currentProjectId);
  currentProjectIdRef.current = currentProjectId;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [creationWalletAddress, setCreationWalletAddress] = useState('');
  const [creationWalletBalance, setCreationWalletBalance] = useState('');
  const [lastVerification, setLastVerification] = useState(null);
  const displayVerification = useMemo(
    () => normalizeLaunchVerification(lastVerification),
    [lastVerification],
  );
  const [verifying, setVerifying] = useState(false);
  /** null = unchecked or server has no explorer API key */
  const [explorerSourceVerified, setExplorerSourceVerified] = useState(null);
  const [checkingExplorerSource, setCheckingExplorerSource] = useState(false);
  /** Bumped by header Refresh so explorer status refetches even if token address unchanged. */
  const [explorerStatusTick, setExplorerStatusTick] = useState(0);

  const [createWalletImportKey, setCreateWalletImportKey] = useState('');
  const [createWalletExportKey, setCreateWalletExportKey] = useState('');
  const [paid, setPaid] = useState(false);
  const [paymentReq, setPaymentReq] = useState(null);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const [hasPaidRecord, setHasPaidRecord] = useState(false);

  const [deployForm, setDeployForm] = useState({
    name: '',
    symbol: '',
    totalSupply: '',
    marketingWallet: '',
    marketingFeeBuy: '',
    marketingFeeSell: '',
    marketingFeeTransfer: '',
  });

  const [creationQrOpen, setCreationQrOpen] = useState(false);
  const [generateWalletDialogOpen, setGenerateWalletDialogOpen] = useState(false);
  const [generateWalletConsent, setGenerateWalletConsent] = useState(false);
  const [creationSendTo, setCreationSendTo] = useState('');
  const [creationSendAmount, setCreationSendAmount] = useState('');
  const [creationSending, setCreationSending] = useState(false);
  const [creationMaxing, setCreationMaxing] = useState(false);

  const cfg = useMemo(() => {
    void currentProjectId;
    return getConfig();
  }, [currentProjectId]);
  const rpcUrl = cfg?.network?.rpcUrl;
  const routerAddress = cfg?.network?.routerAddress;

  /** Assigned launch token from GET /api/me/settings (not localStorage). */
  const tokenAddress = useMemo(
    () => (projectSettings?.token_address || projectSettings?.tokenAddress || '').toString().trim(),
    [projectSettings],
  );
  const tokenMeta = useMemo(
    () => ({
      name: (projectSettings?.token_name || projectSettings?.tokenName || '').toString(),
      symbol: (projectSettings?.token_symbol || projectSettings?.tokenSymbol || '').toString(),
      totalSupply: (projectSettings?.token_total_supply || projectSettings?.tokenTotalSupply || '').toString(),
    }),
    [projectSettings],
  );

  const getWorkingProvider = useCallback(async () => {
    const { ethers } = await import('ethers');
    const chainId = Number(cfg?.network?.chainId || 1);
    const network = { name: chainId === 1 ? 'homestead' : `chain-${chainId}`, chainId };

    const candidates = [
      'https://ethereum-rpc.publicnode.com',
      'https://rpc.ankr.com/eth',
      'https://ethereum-public.nodies.app',
      'https://cloudflare-eth.com',
      rpcUrl,
    ]
      .map((u) => (u ? String(u).trim() : ''))
      .filter(Boolean);

    const withTimeout = (p, ms) =>
      Promise.race([
        p,
        new Promise((_, rej) => setTimeout(() => rej(new Error('RPC timeout')), ms)),
      ]);

    let lastErr = null;
    for (const url of candidates) {
      try {
        const provider = new ethers.providers.JsonRpcProvider(url, network);
        // Probe with a cheap call; avoids some RPCs intermittently failing on getBlockByNumber("latest").
        await withTimeout(provider.send('eth_blockNumber', []), 8000);
        return provider;
      } catch (e) {
        lastErr = e;
      }
    }
    throw lastErr || new Error('No RPC endpoint is reachable');
  }, [cfg?.network?.chainId, rpcUrl]);

  const getWorkingProviderRef = useRef(getWorkingProvider);
  getWorkingProviderRef.current = getWorkingProvider;

  const chainIdHint = useMemo(() => Number(cfg?.network?.chainId ?? 1), [cfg?.network?.chainId]);

  const explorerBase = useMemo(() => {
    const chainId = Number(cfg?.network?.chainId || 1);
    // Ethereum mainnet
    if (chainId === 1) return 'https://etherscan.io';
    // Ethereum Sepolia (common devnet)
    if (chainId === 11155111) return 'https://sepolia.etherscan.io';

    // Fallback heuristics (legacy)
    const u = String(rpcUrl || '').toLowerCase();
    if (u.includes('testnet')) return 'https://testnet.bscscan.com';
    if (u.includes('bsc')) return 'https://bscscan.com';
    return 'https://etherscan.io';
  }, [cfg?.network?.chainId, rpcUrl]);

  const tokenAlreadyDeployed = !!(tokenAddress || '').toString().trim();

  /** Explorer says verified, or last verify response indicates already-verified source */
  const showsExplorerVerified =
    explorerSourceVerified === true || displayVerification?.status === 'already_verified';
  const canDeploy =
    !!creationWalletAddress && !!paid && !!rpcUrl && !!routerAddress && !tokenAlreadyDeployed;

  const stepIndex = useMemo(() => {
    if (!creationWalletAddress) return 0;
    if (!paid) return 1;
    return 2;
  }, [creationWalletAddress, paid]);

  const [activeStep, setActiveStep] = useState(0);

  // Default to the “current progress” step, but don’t fight user navigation.
  useEffect(() => {
    setActiveStep((prev) => (typeof prev === 'number' && prev >= 0 ? prev : stepIndex));
  }, [stepIndex]);

  const apiFetch = useCallback(
    async (path, init = {}) => {
      const token = await getTokenRef.current();
      if (!token) throw new Error('Not signed in');
      if (!currentProjectId) throw new Error('Select a project first');

      const res = await fetch(`${defaultConfig.SERVER_URL.url}${path}`, {
        ...init,
        headers: {
          ...(init.headers || {}),
          Authorization: `Bearer ${token}`,
          'X-Project-Id': currentProjectId,
        },
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
      return data;
    },
    [currentProjectId],
  );

  const refreshExplorerSourceStatus = useCallback(async () => {
    const a = (tokenAddress || '').trim();
    if (!a) {
      setExplorerSourceVerified(null);
      return;
    }
    setCheckingExplorerSource(true);
    try {
      const data = await apiFetch(
        `/api/me/launch/contract-verification-status?address=${encodeURIComponent(a)}`,
      );
      if (data?.checked && data?.verified === true) setExplorerSourceVerified(true);
      else if (data?.checked && data?.verified === false) setExplorerSourceVerified(false);
      else setExplorerSourceVerified(null);
    } catch {
      setExplorerSourceVerified(null);
    } finally {
      setCheckingExplorerSource(false);
    }
  }, [apiFetch, tokenAddress]);

  useEffect(() => {
    void refreshExplorerSourceStatus();
  }, [refreshExplorerSourceStatus, explorerStatusTick]);

  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const refreshProjectSettingsRef = useRef(refreshProjectSettings);
  refreshProjectSettingsRef.current = refreshProjectSettings;

  /** Stable identity (uses refs): avoids Clerk/context callback churn rerunning mounts effects every frame. */
  const applyWizardBackendState = useCallback(async ({ syncProjectSettings }) => {
    const pid = currentProjectIdRef.current;
    if (!pid) return;
    setRefreshing(true);
    try {
      if (syncProjectSettings) {
        await refreshProjectSettingsRef.current();
      }
      const [cw, payStatus] = await Promise.all([
        apiFetchRef.current('/api/me/creation-wallet'),
        apiFetchRef.current('/api/me/project-payment/status').catch(() => ({ paid: false })),
      ]);
      setCreationWalletAddress(cw?.creationWallet?.address || '');
      setCreationWalletBalance('');
      setPaid(!!payStatus?.paid);
      const payment = payStatus?.payment || null;
      const whitelisted = payment?.status === 'whitelisted';
      setIsWhitelisted(!!whitelisted);
      setHasPaidRecord(!!payStatus?.paid && !whitelisted);
    } catch {
      // ignore; individual actions will surface errors
    } finally {
      setRefreshing(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await applyWizardBackendState({ syncProjectSettings: true });
    setExplorerStatusTick((t) => t + 1);
  }, [applyWizardBackendState]);

  const loadPaymentRequirements = useCallback(async () => {
    try {
      const req = await apiFetchRef.current('/api/me/project-payment/requirements');
      setPaymentReq(req);
    } catch {
      setPaymentReq(null);
    }
  }, []);

  useEffect(() => {
    if (!currentProjectId) return;
    setLastVerification(null);
    setExplorerSourceVerified(null);
    // ProjectProvider already syncs `/api/me/settings` on project change — avoid duplicate + context storms.
    void applyWizardBackendState({ syncProjectSettings: false });
    void loadPaymentRequirements();
  }, [currentProjectId, applyWizardBackendState, loadPaymentRequirements]);

  useEffect(() => {
    const loadBalance = async () => {
      if (!creationWalletAddress || !rpcUrl) return;
      try {
        const { ethers } = await import('ethers');
        const provider = await getWorkingProviderRef.current();
        const bal = await provider.getBalance(creationWalletAddress);
        setCreationWalletBalance(ethers.utils.formatEther(bal));
      } catch {
        setCreationWalletBalance('');
      }
    };
    loadBalance();
  }, [creationWalletAddress, rpcUrl, currentProjectId, chainIdHint]);

  const handleOpenCreationQr = () => {
    setCreationSendTo('');
    setCreationSendAmount('');
    setCreationQrOpen(true);
  };

  const creationSendToOk = useMemo(() => {
    const to = creationSendTo.trim();
    return !to ? null : /^0x[a-fA-F0-9]{40}$/.test(to);
  }, [creationSendTo]);

  const creationSendAmountOk = useMemo(() => {
    const amt = creationSendAmount.trim();
    if (!amt) return null;
    const n = Number(amt);
    return Number.isFinite(n) && n > 0;
  }, [creationSendAmount]);

  const handleCreationMax = useCallback(async () => {
    if (!creationWalletAddress || !rpcUrl) return;
    setCreationMaxing(true);
    try {
      const { ethers } = await import('ethers');
      const provider = await getWorkingProvider();
      const fee = await provider.getFeeData().catch(() => null);
      const bal = await provider.getBalance(creationWalletAddress);
      const gasLimit = ethers.BigNumber.from(21000);
      const gasPrice = fee?.maxFeePerGas || fee?.gasPrice || ethers.utils.parseUnits('2', 'gwei');
      const gasCost = gasLimit.mul(gasPrice);
      const maxWei = bal.gt(gasCost) ? bal.sub(gasCost) : ethers.BigNumber.from(0);
      const maxEth = ethers.utils.formatEther(maxWei);
      const buffered = Math.max(0, Number(maxEth) - 0.00001);
      setCreationSendAmount(buffered.toFixed(6));
    } catch (e) {
      toast.error(e?.message || 'Failed to calculate max');
    } finally {
      setCreationMaxing(false);
    }
  }, [creationWalletAddress, rpcUrl, getWorkingProvider]);

  const handleSendFromCreation = useCallback(async () => {
    const to = creationSendTo.trim();
    const amt = creationSendAmount.trim();
    if (!creationWalletAddress) return toast.error('Creation wallet is not set');
    if (!to) return toast.error('Destination address is required');
    const n = Number(amt);
    if (!Number.isFinite(n) || n <= 0) return toast.error('Amount must be > 0');

    setCreationSending(true);
    try {
      const { ethers } = await import('ethers');
      const exported = await apiFetch('/api/me/creation-wallet/export');
      const pk = (exported?.privateKey || '').toString().trim();
      if (!pk) throw new Error('Failed to export creation wallet key');
      const normalized = pk.startsWith('0x') ? pk : `0x${pk}`;

      const provider = await getWorkingProvider();
      const signer = new ethers.Wallet(normalized, provider);
      const fee = await provider.getFeeData().catch(() => null);
      const network = await provider.getNetwork();
      const nonce = await signer.getTransactionCount('pending');
      const value = ethers.utils.parseEther(amt);
      const use1559 = !!fee?.maxFeePerGas;

      const txReq = {
        to,
        value,
        gasLimit: 21000,
        chainId: network.chainId,
        nonce,
        ...(use1559 ? { type: 2 } : { type: 0 }),
        ...(use1559 ? { maxFeePerGas: fee.maxFeePerGas } : {}),
        ...(use1559 && fee?.maxPriorityFeePerGas ? { maxPriorityFeePerGas: fee.maxPriorityFeePerGas } : {}),
        ...(!use1559 && fee?.gasPrice ? { gasPrice: fee.gasPrice } : {}),
      };

      const signedTx = await signer.signTransaction(txReq);
      const token = await getTokenRef.current();
      const res = await fetch(`${defaultConfig.SERVER_URL.url}/api/send-bundle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(currentProjectId ? { 'X-Project-Id': currentProjectId } : {}),
        },
        body: JSON.stringify({ txs: [signedTx], chainId: 1 }),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);

      toast.success(data?.bundleHash ? `Submitted: ${String(data.bundleHash).slice(0, 12)}…` : 'Submitted');
      setCreationQrOpen(false);
    } catch (e) {
      toast.error(e?.message || 'Transfer failed');
    } finally {
      setCreationSending(false);
    }
  }, [apiFetch, creationSendAmount, creationSendTo, creationWalletAddress, currentProjectId, getWorkingProvider]);

  const openGenerateWalletDialog = useCallback(() => {
    setGenerateWalletConsent(false);
    setGenerateWalletDialogOpen(true);
  }, []);

  const handleGenerateCreationWallet = async () => {
    setGenerateWalletDialogOpen(false);
    setGenerateWalletConsent(false);
    setLoading(true);
    try {
      const res = await apiFetch('/api/me/creation-wallet/generate', { method: 'POST' });
      setCreationWalletAddress(res?.creationWallet?.address || '');
      toast.success('Creation wallet generated');

      // Automatically snapshot private key via export endpoint.
      try {
        const exported = await apiFetch('/api/me/creation-wallet/export');
        if (exported?.privateKey) {
          addRecoverySnapshot({
            projectId: currentProjectId,
            type: 'creationWallet',
            label: 'Creation wallet exported (auto)',
            data: { address: exported.address, privateKey: exported.privateKey },
          });
        }
      } catch {
        // ignore auto snapshot failure
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to generate creation wallet');
    } finally {
      setLoading(false);
    }
  };

  const handleImportCreationWallet = async () => {
    const pk = createWalletImportKey.trim();
    if (!pk) return toast.error('Paste a private key to import');
    setLoading(true);
    try {
      const res = await apiFetch('/api/me/creation-wallet/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ privateKey: pk }),
      });
      setCreationWalletAddress(res?.creationWallet?.address || '');
      setCreateWalletImportKey('');
      setCreateWalletExportKey('');
      toast.success('Creation wallet imported');

      try {
        const exported = await apiFetch('/api/me/creation-wallet/export');
        if (exported?.privateKey) {
          addRecoverySnapshot({
            projectId: currentProjectId,
            type: 'creationWallet',
            label: 'Creation wallet exported (auto)',
            data: { address: exported.address, privateKey: exported.privateKey },
          });
        }
      } catch {
        // ignore auto snapshot failure
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to import creation wallet');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCreationWallet = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/me/creation-wallet/export');
      setCreateWalletExportKey(res?.privateKey || '');
      if (res?.privateKey) {
        addRecoverySnapshot({
          projectId: currentProjectId,
          type: 'creationWallet',
          label: 'Creation wallet exported',
          data: { address: res.address, privateKey: res.privateKey },
        });
      }
      toast.success('Creation wallet private key loaded');
    } catch (e) {
      toast.error(e?.message || 'Failed to export creation wallet');
    } finally {
      setLoading(false);
    }
  };

  const handlePay600 = async () => {
    if (!creationWalletAddress) return toast.error('Generate the creation wallet first');
    setLoading(true);
    try {
      const res = await apiFetch('/api/me/project-payment/pay-usdt', { method: 'POST' });
      if (res?.alreadyPaid) {
        toast.success('Already paid for this project');
      } else {
        toast.success('Payment successful');
      }
      setPaid(true);
      await refresh();
    } catch (e) {
      toast.error(e?.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeployToken = async () => {
    if ((tokenAddress || '').toString().trim()) {
      return toast.error('This project already has a token. Create a new project to deploy another.');
    }
    if (!creationWalletAddress) return toast.error('Generate/import a creation wallet first');
    if (!paid) return toast.error('Payment required to unlock token deployment');
    if (!rpcUrl) return toast.error('Missing RPC URL in Configuration');
    if (!routerAddress) return toast.error('Missing router address in Configuration');
    const name = deployForm.name.trim();
    const symbol = deployForm.symbol.trim();
    const totalSupply = deployForm.totalSupply.trim();
    const marketingWallet = deployForm.marketingWallet.trim();
    const marketingFeeBuy = deployForm.marketingFeeBuy.trim();
    const marketingFeeSell = deployForm.marketingFeeSell.trim();
    const marketingFeeTransfer = deployForm.marketingFeeTransfer.trim();
    if (!name || !symbol || !totalSupply) return toast.error('Name, symbol, and total supply are required');

    setLoading(true);
    try {
      const res = await apiFetch('/api/me/launch/deploy-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          symbol,
          totalSupply,
          rpcUrl,
          routerAddress,
          marketingWallet,
          marketingFeeBuy,
          marketingFeeSell,
          marketingFeeTransfer,
        }),
      });
      toast.success('Token deployed and assigned to project');
      setLastVerification(normalizeLaunchVerification(res?.verification || null));
      await refresh();
    } catch (e) {
      toast.error(e?.message || 'Failed to deploy token');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyToken = async () => {
    if (!tokenAddress) return toast.error('No token address to verify');
    if (!tokenMeta?.name || !tokenMeta?.symbol || !tokenMeta?.totalSupply) {
      return toast.error('Missing token metadata (refresh and try again)');
    }
    if (!routerAddress) return toast.error('Missing router address');

    const marketingWallet = (deployForm.marketingWallet || creationWalletAddress || '').trim();
    const marketingFeeBuy = (deployForm.marketingFeeBuy ?? '').toString().trim();
    const marketingFeeSell = (deployForm.marketingFeeSell ?? '').toString().trim();
    const marketingFeeTransfer = (deployForm.marketingFeeTransfer ?? '').toString().trim();
    if (!marketingWallet) return toast.error('Enter marketing wallet (or refresh)');

    setVerifying(true);
    try {
      const res = await apiFetch('/api/me/launch/verify-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contractAddress: tokenAddress,
          name: tokenMeta.name,
          symbol: tokenMeta.symbol,
          totalSupply: tokenMeta.totalSupply,
          routerAddress,
          marketingWallet,
          marketingFeeBuy,
          marketingFeeSell,
          marketingFeeTransfer,
        }),
      });
      const vNorm = normalizeLaunchVerification(res?.verification || null);
      setLastVerification(vNorm);
      if (vNorm?.status === 'submitted') {
        toast.success(`Verification submitted: ${String(vNorm.guid).slice(0, 10)}…`);
      } else if (vNorm?.status === 'already_verified') {
        toast.success('Contract source is already verified on the explorer.');
        setExplorerSourceVerified(true);
      } else if (vNorm?.status === 'skipped') {
        toast.info(`Verification skipped: ${vNorm?.reason || 'unknown'}`);
      } else {
        toast.error(vNorm?.error || 'Verification failed');
      }
    } catch (e) {
      toast.error(e?.message || 'Verification failed');
    } finally {
      setVerifying(false);
      setExplorerStatusTick((t) => t + 1);
    }
  };

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2.5, alignItems: { sm: 'flex-end' }, justifyContent: 'space-between' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              letterSpacing: 0.6,
              backgroundImage: `linear-gradient(90deg, ${accentPurple} 0%, #6366f1 45%, ${accentCyan} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Launch Wizard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 520 }}>
            Creation wallet → payment unlock → deploy your LaunchToken. One token per project.
          </Typography>
        </Box>
        <Tooltip title="Reload payment + creation wallet from server">
          <IconButton
            size="small"
            onClick={() => void refresh()}
            disabled={refreshing || loading}
            sx={{
              alignSelf: { xs: 'flex-start', sm: 'center' },
              border: `1px solid ${alpha('#fff', 0.14)}`,
              borderRadius: 2,
              bgcolor: alpha('#fff', 0.03),
            }}
          >
            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      <MotionPaper
        elevation={0}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${alpha(accentPurple, 0.22)}`,
          background: `linear-gradient(165deg, ${alpha(accentPurple, 0.065)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 45%, ${alpha(accentCyan, 0.04)} 100%)`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: { xs: 2, sm: 2.25 },
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 750, letterSpacing: 2 }}>
              Project token
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {tokenAddress ? (
                <Box component="span" sx={{ display: 'block' }}>
                  <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>
                    {tokenMeta?.name || 'Token'}
                    {tokenMeta?.symbol ? ` (${tokenMeta.symbol})` : ''}
                  </Box>
                  <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, opacity: 0.85, mt: 0.5, display: 'block', wordBreak: 'break-all', fontSize: 13 }}>
                    {tokenAddress}
                  </Box>
                </Box>
              ) : (
                <>No token assigned yet — deploy in step 3 after payment.</>
              )}
            </Typography>
            {displayVerification ? (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                {displayVerification.status === 'already_verified'
                  ? 'Last action: source already verified on the explorer.'
                  : displayVerification.status === 'submitted'
                    ? `Verification submitted (${String(displayVerification.guid || '').slice(0, 12)}…) — confirm on explorer.`
                    : `Last verification: ${String(displayVerification.status || '—')}${
                        displayVerification.guid ? ` • ${String(displayVerification.guid).slice(0, 12)}…` : ''
                      }`}
              </Typography>
            ) : null}
          </Box>
          <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
            <Chip
              size="small"
              label={rpcUrl ? 'RPC ready' : 'RPC missing'}
              sx={{
                fontWeight: 750,
                borderRadius: 2,
                border: `1px solid ${rpcUrl ? alpha(accentCyan, 0.35) : alpha(theme.palette.warning.main, 0.4)}`,
                bgcolor: rpcUrl ? alpha(accentCyan, 0.1) : alpha(theme.palette.warning.main, 0.1),
              }}
            />
            <Chip
              size="small"
              label={routerAddress ? 'Router ready' : 'Router missing'}
              sx={{
                fontWeight: 750,
                borderRadius: 2,
                border: `1px solid ${routerAddress ? alpha(accentPurple, 0.35) : alpha(theme.palette.warning.main, 0.4)}`,
                bgcolor: routerAddress ? alpha(accentPurple, 0.1) : alpha(theme.palette.warning.main, 0.1),
              }}
            />
            {tokenAddress ? (
              <>
                {checkingExplorerSource ? (
                  <CircularProgress size={14} sx={{ color: alpha(accentCyan, 0.85), mx: 0.25 }} />
                ) : showsExplorerVerified ? (
                  <Chip
                    size="small"
                    label="Source verified"
                    sx={{
                      fontWeight: 780,
                      borderRadius: 2,
                      color: alpha(theme.palette.success.light, 0.98),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.45)}`,
                      bgcolor: alpha(theme.palette.success.main, 0.12),
                    }}
                  />
                ) : explorerSourceVerified === false ? (
                  <Chip
                    size="small"
                    label="Not verified on explorer"
                    sx={{
                      fontWeight: 780,
                      borderRadius: 2,
                      color: alpha(theme.palette.warning.light, 0.95),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.38)}`,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                    }}
                  />
                ) : null}
                {showsExplorerVerified ? (
                  <Tooltip title="Etherscan already lists verified source. Use this only if the explorer looks wrong or you changed bytecode.">
                    <Button
                      size="small"
                      variant="text"
                      disabled={verifying}
                      onClick={handleVerifyToken}
                      sx={{
                        ...pillSx,
                        py: 0.85,
                        minWidth: 0,
                        color: alpha(theme.palette.success.light, 0.92),
                        fontWeight: 700,
                      }}
                    >
                      {verifying ? 'Checking…' : 'Submit again'}
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={verifying}
                    onClick={handleVerifyToken}
                    sx={{ ...pillSx, borderColor: alpha(accentPurple, 0.45), py: 0.85 }}
                  >
                    {verifying ? 'Verifying…' : 'Verify'}
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  component="a"
                  href={`${explorerBase}/address/${tokenAddress}`}
                  target="_blank"
                  rel="noreferrer"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  sx={{ ...pillSx, borderColor: alpha(accentCyan, 0.42), py: 0.85 }}
                >
                  Explorer
                </Button>
              </>
            ) : null}
          </Stack>
        </Box>

        <Box sx={{ px: { xs: 1.75, sm: 2 }, py: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2 } }}>
          <Stepper
            nonLinear
            activeStep={activeStep}
            orientation="vertical"
            sx={{
              '& .MuiStepConnector-root.Mui-vertical .MuiStepConnector-line': {
                borderLeftWidth: 2,
                borderLeftColor: alpha(accentPurple, 0.28),
                minHeight: 12,
              },
              '& .Mui-active .MuiStepLabel-label, & .Mui-completed .MuiStepLabel-label': {
                fontWeight: 850,
              },
              '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
                color: accentPurple,
              },
            }}
          >
            <Step>
              <StepButton onClick={() => setActiveStep(0)}>
                <StepLabel>
                  <Typography sx={{ fontWeight: 850 }}>Creation wallet (C‑Master)</Typography>
                </StepLabel>
              </StepButton>
              <StepContent>
                <Card
                  variant="outlined"
                  sx={{
                    mb: 2,
                    borderRadius: 3,
                    borderColor: creationWalletAddress
                      ? alpha(theme.palette.success.main, 0.38)
                      : alpha(accentPurple, 0.28),
                    background: creationWalletAddress
                      ? `linear-gradient(120deg, ${alpha(theme.palette.success.main, 0.09)} 0%, transparent 55%)`
                      : `linear-gradient(120deg, ${alpha(accentPurple, 0.1)} 0%, transparent 55%)`,
                  }}
                >
                  <CardContent sx={{ py: 1.75 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>
                      CREATION WALLET
                    </Typography>
                    {creationWalletAddress ? (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, flexWrap: 'wrap' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 999,
                            border: '1px solid rgba(255,255,255,0.10)',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            minWidth: 0,
                            maxWidth: '100%',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 800 }} noWrap>
                            {creationWalletAddress}
                          </Typography>
                          <Tooltip title="Copy">
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(creationWalletAddress);
                                toast.success('Copied');
                              }}
                              sx={{ ml: 0.25 }}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {creationWalletBalance ? (
                            <Chip size="small" label={`${Number(creationWalletBalance).toFixed(4)} ETH`} sx={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
                          ) : null}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleOpenCreationQr}
                            sx={{ borderRadius: 999, px: 1.5 }}
                          >
                            Send
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            component="a"
                            href={`${explorerBase}/address/${creationWalletAddress}`}
                            target="_blank"
                            rel="noreferrer"
                            endIcon={<OpenInNewIcon fontSize="small" />}
                            sx={{ borderRadius: 999, px: 1.5 }}
                          >
                            Explorer
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ mt: 1, fontWeight: 750 }}>
                        Not created yet
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Used to deploy the token and hold LP tokens. Fund it directly (e.g. from Binance) when needed.
                    </Typography>
                  </CardContent>
                </Card>

                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={openGenerateWalletDialog}
                    disabled={loading || hasPaidRecord}
                    sx={{
                      ...gradientPrimarySx,
                      py: 1,
                      px: 2.5,
                    }}
                  >
                    {loading ? <CircularProgress size={18} sx={{ color: '#08090e' }} /> : 'Generate wallet'}
                  </Button>
                  {hasPaidRecord ? <Chip size="small" color="success" label="Locked after payment" /> : null}
                  {isWhitelisted ? <Chip size="small" color="info" label="Whitelisted (payment bypass)" /> : null}
                </Box>

                <Dialog
                  open={generateWalletDialogOpen}
                  onClose={() => {
                    if (loading) return;
                    setGenerateWalletDialogOpen(false);
                    setGenerateWalletConsent(false);
                  }}
                  maxWidth="xs"
                  fullWidth
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      borderRadius: 3,
                      border: `1px solid ${alpha('#fff', 0.1)}`,
                      backgroundImage: `linear-gradient(165deg, ${alpha(accentPurple, 0.08)} 0%, ${alpha(
                        theme.palette.background.paper,
                        0.98,
                      )} 50%, ${alpha(accentCyan, 0.05)} 100%)`,
                      overflow: 'hidden',
                    },
                  }}
                >
                  <DialogTitle component="div" sx={{ px: 3, pt: 2.75, pb: 1 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 900, letterSpacing: 0.2, lineHeight: 1.25 }}>
                      {(creationWalletAddress || '').trim() ? 'Replace creation wallet?' : 'New creation wallet'}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="div"
                      color="text.secondary"
                      sx={{ mt: 1, fontWeight: 500, lineHeight: 1.5 }}
                    >
                      {(creationWalletAddress || '').trim()
                        ? 'The old address will no longer be active for this project.'
                        : 'Used for payment unlock and token deploy. Save your key after creation.'}
                    </Typography>
                  </DialogTitle>
                  <DialogContent sx={{ px: 3, pt: 0, pb: 0 }}>
                    {(creationWalletAddress || '').trim() ? (
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2.25,
                          pr: 0,
                          py: 0,
                          color: 'text.secondary',
                          typography: 'body2',
                          lineHeight: 1.55,
                          '& li': { mb: 1.1 },
                          '& li:last-of-type': { mb: 0 },
                        }}
                      >
                        <Box component="li">
                          Paid from <strong>this</strong> address? A new wallet can break the payment–project link.
                        </Box>
                        <Box component="li">
                          Already <strong>deployed</strong> with an older creation wallet? LP and deploy flows stay with
                          that wallet—a new one does not take over.
                        </Box>
                        <Box component="li">Export and back up keys before you replace it.</Box>
                      </Box>
                    ) : (
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2.25,
                          color: 'text.secondary',
                          typography: 'body2',
                          lineHeight: 1.55,
                          '& li': { mb: 1 },
                          '& li:last-of-type': { mb: 0 },
                        }}
                      >
                        <Box component="li">Open <strong>Advanced</strong> after generating to export your private key.</Box>
                        <Box component="li">We cannot recover funds if you lose access to that key.</Box>
                      </Box>
                    )}
                    <Box
                      sx={{
                        mt: 2.25,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                      }}
                    >
                      <FormControlLabel
                        sx={{ m: 0, alignItems: 'flex-start', gap: 1, '& .MuiTypography-root': { fontSize: 13, lineHeight: 1.45 } }}
                        control={
                          <Checkbox
                            size="small"
                            checked={generateWalletConsent}
                            onChange={(e) => setGenerateWalletConsent(e.target.checked)}
                            color="primary"
                            sx={{ py: 0 }}
                          />
                        }
                        label="I understand and want to continue."
                      />
                    </Box>
                  </DialogContent>
                  <DialogActions
                    sx={{
                      px: 3,
                      py: 2.25,
                      pt: 2,
                      gap: 1,
                      flexWrap: 'wrap',
                      borderTop: `1px solid ${alpha('#fff', 0.06)}`,
                      bgcolor: alpha('#000', 0.15),
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setGenerateWalletDialogOpen(false);
                        setGenerateWalletConsent(false);
                      }}
                      disabled={loading}
                      sx={{ borderRadius: 999, px: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                      Cancel
                    </Button>
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Button
                      variant="contained"
                      disableElevation
                      disabled={loading || !generateWalletConsent}
                      onClick={() => void handleGenerateCreationWallet()}
                      sx={{
                        ...gradientPrimarySx,
                        borderRadius: 999,
                        px: 2.5,
                        minWidth: 148,
                        '&.Mui-disabled': {
                          backgroundImage: 'none',
                          bgcolor: alpha('#fff', 0.08),
                          color: alpha('#fff', 0.35),
                        },
                      }}
                    >
                      {loading ? <CircularProgress size={18} sx={{ color: '#08090e' }} /> : 'Continue'}
                    </Button>
                  </DialogActions>
                </Dialog>

                <Accordion
                  sx={{
                    mt: 2,
                    borderRadius: 3,
                    border: `1px solid ${alpha(accentPurple, 0.2)}`,
                    backgroundColor: alpha('#fff', 0.02),
                    '&:before': { display: 'none' },
                  }}
                  disableGutters
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Advanced: import / export keys
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'grid', gap: 1.25, maxWidth: 720 }}>
                      <TextField
                        label="Import private key"
                        value={createWalletImportKey}
                        onChange={(e) => setCreateWalletImportKey(e.target.value)}
                        placeholder="0x…"
                      />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button variant="outlined" onClick={handleImportCreationWallet} disabled={loading || hasPaidRecord}>
                          Import
                        </Button>
                        <Button variant="outlined" onClick={handleExportCreationWallet} disabled={loading}>
                          Export
                        </Button>
                      </Box>
                      <TextField label="Exported private key" value={createWalletExportKey} InputProps={{ readOnly: true }} />
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Dialog
                  open={creationQrOpen}
                  onClose={() => (creationSending ? null : setCreationQrOpen(false))}
                  maxWidth="sm"
                  fullWidth
                >
                  <DialogTitle sx={{ fontWeight: 900 }}>Creation wallet</DialogTitle>
                  <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                      Address: <span style={{ fontFamily: 'monospace' }}>{creationWalletAddress || '—'}</span>
                    </Typography>
                    {creationWalletAddress ? (
                      <Box sx={{ display: 'grid', placeItems: 'center', py: 1.5 }}>
                        <QRCodeCanvas value={creationWalletAddress || ''} size={240} />
                      </Box>
                    ) : null}

                    <Divider sx={{ my: 2 }} />

                    <TextField
                      label="To"
                      value={creationSendTo}
                      onChange={(e) => setCreationSendTo(e.target.value)}
                      fullWidth
                      disabled={creationSending}
                      error={creationSendToOk === false}
                      helperText={creationSendToOk === false ? 'Invalid address' : ' '}
                    />
                    <TextField
                      label="Amount (ETH)"
                      value={creationSendAmount}
                      onChange={(e) => setCreationSendAmount(e.target.value)}
                      fullWidth
                      disabled={creationSending}
                      error={creationSendAmountOk === false}
                      helperText={creationSendAmountOk === false ? 'Enter a valid amount' : ' '}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              size="small"
                              onClick={handleCreationMax}
                              disabled={creationSending || creationMaxing}
                              sx={{ borderRadius: 999, minWidth: 0, px: 1.25 }}
                            >
                              {creationMaxing ? <CircularProgress size={14} /> : 'MAX'}
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </DialogContent>
                  <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setCreationQrOpen(false)} disabled={creationSending} sx={{ borderRadius: 999 }}>
                      Close
                    </Button>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={handleSendFromCreation}
                      disabled={creationSending || !creationWalletAddress || creationSendToOk !== true || creationSendAmountOk !== true}
                      sx={{ ...gradientPrimarySx, py: 1 }}
                    >
                      {creationSending ? 'Sending…' : 'Send'}
                    </Button>
                  </DialogActions>
                </Dialog>
              </StepContent>
            </Step>

            <Step>
              <StepButton onClick={() => setActiveStep(1)}>
                <StepLabel>
                  <Typography sx={{ fontWeight: 850 }}>Payment</Typography>
                </StepLabel>
              </StepButton>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Step 2/3. A one-time payment of{' '}
                  <b>
                    {paymentReq?.amountTokens ?? 600} {paymentReq?.tokenSymbol ?? 'USDT'}
                  </b>{' '}
                  is required per launch (per project). Pay from the generated creation wallet. After payment, this project's creation wallet is locked.
                </Typography>

                {paid ? (
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      borderRadius: 3,
                      borderColor: 'rgba(34,197,94,0.35)',
                      background: 'linear-gradient(90deg, rgba(34,197,94,0.08) 0%, rgba(13,15,24,0.0) 60%)',
                    }}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        Payment confirmed
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        This project is unlocked for deployment and bundle actions.
                      </Typography>
                    </CardContent>
                  </Card>
                ) : (
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      borderRadius: 3,
                      borderColor: 'rgba(245,158,11,0.35)',
                      background: 'linear-gradient(90deg, rgba(245,158,11,0.10) 0%, rgba(13,15,24,0.0) 60%)',
                    }}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        Payment required
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        Complete payment to continue.
                      </Typography>
                      {paymentReq ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                          Treasury: {paymentReq.treasuryAddress} • ChainId: {paymentReq.chainId}
                        </Typography>
                      ) : null}
                    </CardContent>
                  </Card>
                )}

                <Button
                  variant="contained"
                  disableElevation
                  onClick={handlePay600}
                  disabled={loading || paid || !creationWalletAddress}
                  sx={{ ...gradientPrimarySx, py: 1.25, minWidth: 160 }}
                >
                  {loading ? <CircularProgress size={20} sx={{ color: '#08090e' }} /> : 'Pay now'}
                </Button>
              </StepContent>
            </Step>

            <Step>
              <StepButton onClick={() => setActiveStep(2)}>
                <StepLabel>
                  <Typography sx={{ fontWeight: 850 }}>Deploy token</Typography>
                </StepLabel>
              </StepButton>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Deploys from the creation wallet and automatically assigns the token to this project.{' '}
                  <strong>One token per project</strong> — after deployment you cannot deploy again here.
                </Typography>

                {tokenAlreadyDeployed ? (
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      borderRadius: 3,
                      borderColor: 'rgba(34,197,94,0.35)',
                      background: 'linear-gradient(90deg, rgba(34,197,94,0.10) 0%, rgba(13,15,24,0.0) 60%)',
                    }}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 750 }}>
                        Token already deployed for this project
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                        Your project token is set (see Project token above). To launch another token, create a new
                        project in the project selector.
                      </Typography>
                    </CardContent>
                  </Card>
                ) : null}

                {!canDeploy && !tokenAlreadyDeployed ? (
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      borderRadius: 3,
                      borderColor: 'rgba(245,158,11,0.35)',
                      background: 'linear-gradient(90deg, rgba(245,158,11,0.10) 0%, rgba(13,15,24,0.0) 60%)',
                    }}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 750 }}>
                        Missing prerequisites
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        Create wallet + complete payment + configure RPC + router before deploying.
                      </Typography>
                    </CardContent>
                  </Card>
                ) : null}

                <Box
                  sx={{
                    display: 'grid',
                    gap: 1.25,
                    maxWidth: 520,
                    opacity: tokenAlreadyDeployed ? 0.45 : 1,
                    pointerEvents: tokenAlreadyDeployed ? 'none' : 'auto',
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  }}
                >
                  <TextField
                    label="Token name"
                    value={deployForm.name}
                    onChange={(e) => setDeployForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Parroty"
                  />
                  <TextField
                    label="Token symbol"
                    value={deployForm.symbol}
                    onChange={(e) => setDeployForm((p) => ({ ...p, symbol: e.target.value }))}
                    placeholder="PARROTY"
                  />
                  <TextField
                    label="Total supply"
                    value={deployForm.totalSupply}
                    onChange={(e) => setDeployForm((p) => ({ ...p, totalSupply: e.target.value }))}
                    inputProps={{ inputMode: 'decimal' }}
                    placeholder="1000000000"
                    helperText="Whole tokens (18 decimals)."
                  />
                  <TextField
                    label="Marketing wallet"
                    value={deployForm.marketingWallet}
                    onChange={(e) => setDeployForm((p) => ({ ...p, marketingWallet: e.target.value }))}
                    placeholder="0x..."
                    helperText="Receives marketing fees in ETH (after swapback). Leave blank to use the creation wallet."
                  />
                  <TextField
                    label="Buy fee (%)"
                    value={deployForm.marketingFeeBuy}
                    onChange={(e) => setDeployForm((p) => ({ ...p, marketingFeeBuy: e.target.value }))}
                    inputProps={{ inputMode: 'decimal' }}
                    placeholder="30"
                    helperText="Applied on buys only. Values >100 effectively act as 100%."
                  />
                  <TextField
                    label="Sell fee (%)"
                    value={deployForm.marketingFeeSell}
                    onChange={(e) => setDeployForm((p) => ({ ...p, marketingFeeSell: e.target.value }))}
                    inputProps={{ inputMode: 'decimal' }}
                    placeholder="30"
                    helperText="Applied on sells only. Values >100 effectively act as 100%."
                  />
                  <TextField
                    label="Transfer fee (%)"
                    value={deployForm.marketingFeeTransfer}
                    onChange={(e) => setDeployForm((p) => ({ ...p, marketingFeeTransfer: e.target.value }))}
                    inputProps={{ inputMode: 'decimal' }}
                    placeholder="0"
                    helperText="Applied on wallet-to-wallet transfers only."
                  />
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={handleDeployToken}
                    disabled={loading || !canDeploy || tokenAlreadyDeployed}
                    sx={{ ...gradientPrimarySx, py: 1.25, minWidth: 180 }}
                  >
                    {loading ? <CircularProgress size={20} sx={{ color: '#08090e' }} /> : 'Deploy token'}
                  </Button>
                </Box>
              </StepContent>
            </Step>

          </Stepper>
        </Box>
      </MotionPaper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: { xs: 'left', sm: 'center' } }}>
        Creation wallet locks after payment unless whitelisted.
      </Typography>
    </Box>
  );
};

export default LaunchWizard;

